import { Box, Button, TextField, Typography } from '@material-ui/core';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

type Directory = {
  status: string;
  refreshedAt?: string;
  message?: string;
  refreshAfterSeconds?: number;
};
type Suggestion = {
  accountId: string;
  connectionStatus: string;
  validatedAt?: string;
};
type SearchResult = {
  accounts: Suggestion[];
  directory: Directory;
  canConnectUnlisted: boolean;
};
type Props = {
  request: (path: string, init?: RequestInit) => Promise<unknown>;
  onAdd: (accountId: string) => Promise<boolean>;
  canManage: boolean;
  disabled?: boolean;
};

const connectionStatusLabels: Record<string, string> = {
  NOT_CONNECTED: 'Not connected',
  CONNECTED: 'Connected',
  PENDING_VALIDATION: 'Pending validation',
  VALIDATION_FAILED: 'Validation failed',
  PERMISSION_MISSING: 'Permission missing',
  EXPIRED: 'Expired',
};

function object(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseDirectory(value: unknown): Directory {
  if (
    !object(value) ||
    typeof value.status !== 'string' ||
    !['READY', 'PARTIAL', 'UNAVAILABLE'].includes(value.status)
  ) {
    throw new Error('The account directory returned an invalid response.');
  }
  return {
    status: value.status,
    refreshedAt:
      typeof value.refreshedAt === 'string' ? value.refreshedAt : undefined,
    message: typeof value.message === 'string' ? value.message : undefined,
    refreshAfterSeconds:
      typeof value.refreshAfterSeconds === 'number' &&
      Number.isFinite(value.refreshAfterSeconds)
        ? Math.max(60, Math.min(86400, value.refreshAfterSeconds))
        : undefined,
  };
}

function parseSearch(value: unknown, prefix: string): SearchResult {
  if (!object(value) || !Array.isArray(value.accounts)) {
    throw new Error('The account directory returned an invalid response.');
  }
  const accounts = value.accounts.map((item): Suggestion => {
    if (
      !object(item) ||
      typeof item.accountId !== 'string' ||
      !/^\d{12}$/.test(item.accountId) ||
      !item.accountId.startsWith(prefix) ||
      typeof item.connectionStatus !== 'string'
    ) {
      throw new Error('The account directory returned an invalid response.');
    }
    return {
      accountId: item.accountId,
      connectionStatus: item.connectionStatus,
      validatedAt:
        typeof item.validatedAt === 'string' ? item.validatedAt : undefined,
    };
  });
  return {
    accounts: accounts
      .filter(
        (item, index) =>
          accounts.findIndex(other => other.accountId === item.accountId) ===
          index,
      )
      .slice(0, 20),
    directory: parseDirectory(value.directory),
    canConnectUnlisted: value.canConnectUnlisted === true,
  };
}

/** Suggestions are tenant-authorized server results; typing never selects an account. */
export function AwsAccountIdInput({
  request,
  onAdd,
  canManage,
  disabled,
}: Props) {
  const id = useId();
  const generation = useRef(0);
  const pending = useRef<AbortController>();
  const pendingRefresh = useRef<AbortController>();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string>();
  const [result, setResult] = useState<SearchResult>();
  const [directory, setDirectory] = useState<Directory>();
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const [refreshError, setRefreshError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);

  useEffect(() => {
    const version = ++generation.current;
    const controller = new AbortController();
    pending.current?.abort();
    pending.current = controller;
    setResult(undefined);
    setActive(-1);
    setError('');
    setLoading(false);
    if (!/^\d{1,12}$/.test(query) || selected || disabled || refreshing) {
      return () => controller.abort();
    }
    setLoading(true);
    const timer = setTimeout(() => {
      void request(`/aws/accounts?prefix=${encodeURIComponent(query)}`, {
        signal: controller.signal,
      })
        .then(value => {
          if (controller.signal.aborted || generation.current !== version)
            return;
          const parsed = parseSearch(value, query);
          setResult(parsed);
          setDirectory(previous => ({
            ...parsed.directory,
            refreshAfterSeconds:
              parsed.directory.refreshAfterSeconds ??
              previous?.refreshAfterSeconds,
          }));
        })
        .catch(caught => {
          if (controller.signal.aborted || generation.current !== version)
            return;
          setError(
            caught instanceof Error
              ? caught.message
              : 'Account search failed. Try again.',
          );
        })
        .finally(() => {
          if (!controller.signal.aborted && generation.current === version)
            setLoading(false);
        });
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [attempt, disabled, query, refreshing, request, selected]);

  useEffect(
    () => () => {
      generation.current += 1;
      pending.current?.abort();
      pendingRefresh.current?.abort();
    },
    [],
  );

  const choices = result?.accounts ?? [];
  const unlisted =
    canManage &&
    result?.canConnectUnlisted === true &&
    /^\d{12}$/.test(query) &&
    !choices.some(item => item.accountId === query);
  const options = [
    ...choices.map(item => ({
      accountId: item.accountId,
      label: `${item.accountId} — ${
        connectionStatusLabels[item.connectionStatus] ?? 'Unavailable'
      }`,
    })),
    ...(unlisted
      ? [{ accountId: query, label: `Use account ID ${query} (not listed)` }]
      : []),
  ];
  const expanded = open && !selected && options.length > 0;
  useEffect(() => {
    if (expanded && active >= 0) {
      document
        .getElementById(`${id}-option-${active}`)
        ?.scrollIntoView?.({ block: 'nearest' });
    }
  }, [active, expanded, id]);
  const choose = (accountId: string) => {
    generation.current += 1;
    pending.current?.abort();
    setQuery(accountId);
    setSelected(accountId);
    setOpen(false);
    setActive(-1);
    setError('');
  };
  const refresh = useCallback(async () => {
    if (pendingRefresh.current && !pendingRefresh.current.signal.aborted)
      return;
    generation.current += 1;
    pending.current?.abort();
    const controller = new AbortController();
    pendingRefresh.current?.abort();
    pendingRefresh.current = controller;
    setSelected(undefined);
    setRefreshing(true);
    setRefreshError('');
    try {
      const value = await request('/aws/accounts/refresh', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: '{}',
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      setDirectory(
        parseDirectory(
          object(value) && object(value.directory) ? value.directory : value,
        ),
      );
    } catch (caught) {
      if (!controller.signal.aborted) {
        setRefreshError(
          caught instanceof Error
            ? caught.message
            : 'Directory refresh failed. Try again.',
        );
      }
    } finally {
      if (!controller.signal.aborted) {
        setRefreshing(false);
        setAttempt(previous => previous + 1);
        pendingRefresh.current = undefined;
      }
    }
  }, [request]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const refreshAfterSeconds = directory?.refreshAfterSeconds ?? 300;
  useEffect(() => {
    const interval = setInterval(
      () => void refresh(),
      refreshAfterSeconds * 1000,
    );
    return () => clearInterval(interval);
  }, [refresh, refreshAfterSeconds]);
  const add = async () => {
    if (!selected || !canManage || disabled || adding) return;
    setAdding(true);
    try {
      if (await onAdd(selected)) {
        setQuery('');
        setSelected(undefined);
        setResult(undefined);
      }
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : 'Account connection failed. Try again.',
      );
    } finally {
      setAdding(false);
    }
  };

  return (
    <Box mt={2} p={2} border={1} borderColor="divider" borderRadius={4}>
      <Typography variant="subtitle1">Find an AWS account</Typography>
      <TextField
        id={`${id}-account`}
        label="AWS Account ID"
        placeholder="Start typing an AWS account ID"
        variant="outlined"
        margin="normal"
        fullWidth
        value={query}
        disabled={disabled || refreshing || adding}
        autoComplete="off"
        inputProps={{
          type: 'text',
          inputMode: 'numeric',
          maxLength: 12,
          role: 'combobox',
          'aria-autocomplete': 'list',
          'aria-expanded': expanded,
          'aria-controls': expanded ? `${id}-options` : undefined,
          'aria-activedescendant':
            expanded && active >= 0 ? `${id}-option-${active}` : undefined,
        }}
        helperText="Enter 1–12 digits. Choose an authorized account, then select Add account."
        onFocus={() => setOpen(true)}
        onBlur={() => {
          setOpen(false);
          setActive(-1);
        }}
        onChange={event => {
          const next = event.target.value;
          if (!/^\d{0,12}$/.test(next)) return;
          generation.current += 1;
          pending.current?.abort();
          setQuery(next);
          setSelected(undefined);
          setResult(undefined);
          setOpen(true);
          setActive(-1);
        }}
        onKeyDown={event => {
          if (event.key === 'Escape') {
            setOpen(false);
            setActive(-1);
          }
          if (
            (event.key === 'ArrowDown' || event.key === 'ArrowUp') &&
            options.length
          ) {
            event.preventDefault();
            setOpen(true);
            setActive(previous =>
              event.key === 'ArrowDown'
                ? (previous + 1) % options.length
                : (previous <= 0 ? options.length : previous) - 1,
            );
          }
          if (event.key === 'Enter') {
            event.preventDefault();
            if (expanded && active >= 0) choose(options[active].accountId);
          }
        }}
      />
      {expanded && (
        <Box
          component="ul"
          id={`${id}-options`}
          role="listbox"
          aria-label="Authorized AWS accounts"
          m={0}
          p={0}
          style={{ listStyle: 'none', maxHeight: 240, overflowY: 'auto' }}
        >
          {options.map((item, index) => (
            <Box
              component="li"
              key={item.accountId}
              id={`${id}-option-${index}`}
              role="option"
              aria-selected={index === active}
            >
              <Button
                fullWidth
                tabIndex={-1}
                style={{
                  justifyContent: 'flex-start',
                  backgroundColor:
                    index === active ? 'rgba(127,127,127,0.16)' : undefined,
                }}
                onMouseDown={event => event.preventDefault()}
                onClick={() => choose(item.accountId)}
              >
                {item.label}
              </Button>
            </Box>
          ))}
        </Box>
      )}
      {loading && (
        <Typography variant="body2" role="status">
          Searching authorized accounts…
        </Typography>
      )}
      {refreshing && (
        <Typography variant="body2" role="status">
          Refreshing account directory…
        </Typography>
      )}
      {selected && (
        <Typography variant="body2" role="status">
          Selected account ID: {selected}
        </Typography>
      )}
      {error && (
        <Typography color="error" role="alert">
          {error}
        </Typography>
      )}
      {refreshError && (
        <Typography color="error" role="alert">
          Directory refresh: {refreshError}
        </Typography>
      )}
      {directory && (
        <Typography variant="body2">
          Account directory: {directory.status}
          {directory.refreshedAt
            ? ` · Last refreshed ${directory.refreshedAt}`
            : ''}
          {directory.message ? ` · ${directory.message}` : ''}
        </Typography>
      )}
      {result && !loading && !selected && choices.length === 0 && (
        <Typography variant="body2">
          {result.directory.status === 'READY'
            ? 'No authorized accounts match this prefix.'
            : 'No cached accounts match this prefix. The directory is incomplete or unavailable; refresh it or contact your administrator.'}
        </Typography>
      )}
      <Box display="flex" flexWrap="wrap" gridGap={8} mt={1}>
        <Button
          variant="contained"
          color="primary"
          disabled={!canManage || !selected || disabled || adding || refreshing}
          onClick={() => void add()}
        >
          Add account
        </Button>
        <Button
          disabled={disabled || adding || refreshing}
          onClick={() => void refresh()}
        >
          Refresh account directory
        </Button>
        {error && (
          <Button
            disabled={disabled || refreshing || adding || !query}
            onClick={() => setAttempt(previous => previous + 1)}
          >
            Retry account search
          </Button>
        )}
      </Box>
      {!canManage && (
        <Typography variant="body2">
          Only an account manager can add connections. Select an existing
          connected account below.
        </Typography>
      )}
    </Box>
  );
}
